import "simplebar-react/dist/simplebar.min.css";
import "./ScrollBarStyle.css";

import ArticleCard from "@components/Blocks/Card/Article/ArticleCard";
import { ArticleCardVariant } from "@config/articleCard";
import BeyondWordsPlaylist from "@elements/BeyondWordsPlayer/BeyondWordsPlaylist";
import { IS_PLAYWRIGHT_TEST } from "@helper/getEnvVariables";
import { Heading } from "@sphtech/sph-web-ui/elements/Heading";
import { ProcessedArticleData } from "@transformer/useOSResponse";
import { MoreLinkProps } from "@typings/StoryPackage.d";
import { FC, HTMLAttributes } from "react";
import SimpleBar from "simplebar-react";
import { twMerge } from "tailwind-merge";

import MoreLink from "./MoreLink";
import { TitleIconImgProps } from "./VerticalStoryPackage";

export type HorizontalStoryPackageStyles = {
  wrapperStyle?: string;
  titleStyle?: string;
  cardWrapperStyle?: string;
  cardInnerWrapperStyle?: string;
  moreLinkStyle?: string;
  imageWidth?: number;
  imageHeight?: number;
};

export type HorizontalStoryPackageProps = {
  variant: ArticleCardVariant;
  renderItems: ProcessedArticleData[];
  title?: string;
  moreLink?: MoreLinkProps;
  styles?: HorizontalStoryPackageStyles;
  titleIcon?: TitleIconImgProps;
  showBeyondWordsPlayer?: boolean;
};

export default function HorizontalStoryPackage({
  variant = "small",
  title,
  renderItems,
  styles = {
    wrapperStyle: "",
    titleStyle: "",
    moreLinkStyle: "",
  },
  moreLink,
  titleIcon,
  showBeyondWordsPlayer = false,
}: HorizontalStoryPackageProps) {
  let visibleItems = renderItems;
  let sliderWidth = "w-[2150px]";
  if (IS_PLAYWRIGHT_TEST === "true") {
    visibleItems = renderItems.slice(0, 6);
    sliderWidth = "w-[1290px] min-h-[300px]";
  }

  const ScrollWrapper =
    IS_PLAYWRIGHT_TEST === "true"
      ? ({ children, className, style }: HTMLAttributes<HTMLDivElement>) => (
          <div
            className={className}
            style={{ overflowX: "auto", height: "100%", ...style }}
          >
            {children}
          </div>
        )
      : (SimpleBar as FC<HTMLAttributes<HTMLDivElement>>);

  const {
    wrapperStyle,
    titleStyle,
    cardWrapperStyle,
    cardInnerWrapperStyle,
    moreLinkStyle,
    imageWidth = 206,
    imageHeight = 155,
  } = styles;

  return (
    <div
      className={twMerge(
        "my-sm py-xs border border-red-600 border-x-0 border-b-0 border-t-2",
        wrapperStyle,
      )}
    >
      {title && (
        <>
          <div
            className={twMerge(
              "text-sm font-bold text-neutral-700 leading-[24px] mb-xs",
              titleStyle,
            )}
          >
            {titleIcon && (
              <img
                src={titleIcon.src}
                alt="title-icon"
                width={titleIcon.width}
                height={titleIcon.height}
              />
            )}
            <Heading Element="h4" className="font-primary" testId="heading">
              {title}
            </Heading>
          </div>
          {showBeyondWordsPlayer && (
            <BeyondWordsPlaylist
              data={renderItems.map((article) => ({
                id: article.id,
                title: article.title,
              }))}
            />
          )}
        </>
      )}
      <ScrollWrapper
        style={{ height: "100%", overflowX: "auto" }}
        className={twMerge("scrollBarStyles pb-5", cardWrapperStyle)}
      >
        <div
          className={twMerge(
            "flex gap-[10px]",
            sliderWidth,
            cardInnerWrapperStyle,
          )}
        >
          {visibleItems.map((item, idx) => {
            return (
              <ArticleCard
                key={item.id}
                {...item}
                orientation="vertical"
                variant={variant}
                testId={`article-${idx}`}
                forNewDesign={true}
                displayWidth={imageWidth}
                displayHeight={imageHeight}
              />
            );
          })}
        </div>
      </ScrollWrapper>
      {moreLink && (
        <div
          className={twMerge(
            "flex items-center justify-end text-xxs leading-[18px] text-neutral-700 font-bold font-primary border-border-strong border-t-[1px] pt-xs",
            moreLinkStyle,
          )}
        >
          <MoreLink
            {...moreLink}
            className={twMerge("flex gap-1", moreLink.className)}
          />
        </div>
      )}
    </div>
  );
}
